import { cx, css } from "@emotion/css";
import { themeTokens } from "@octopusdeploy/design-system-tokens";
import React, { useCallback, useState } from "react";
import JSONArrow from "./JSONArrow";
import JSONNode from "./JSONNode";
import { doesObjectNodeHaveMatchingDescendants } from "./filterUtils";
import getCollectionEntries from "./getCollectionEntries";
import type { CommonInternalProps } from "./types";
interface RenderChildNodesProps extends CommonInternalProps {
    data: unknown;
    level: number;
}
function renderChildNodes(props: RenderChildNodesProps) {
    const { data, keyPath, filterValue } = props;
    const childNodes: React.ReactNode[] = [];
    getCollectionEntries("Object", data).forEach((entry) => {
        const { key, value } = entry;
        const childKeyPath = [key, ...keyPath];
        // Only render child if it matches the filter (or if no filter is applied)
        if (doesObjectNodeHaveMatchingDescendants(filterValue || "", value, childKeyPath)) {
            childNodes.push(<JSONNode {...props} key={`Node--${key}`} keyPath={childKeyPath} value={value} hideRoot={false}/>);
        }
    });
    return childNodes;
}
interface Props extends CommonInternalProps {
    data: unknown;
    nodeTypeIndicator: string;
    createItemString: (data: unknown) => string;
    expandable: boolean;
}
export default function JSONNestedNode(props: Props) {
    const { createItemString, data, expandable, hideRoot, keyPath, level = 0, nodeTypeIndicator, shouldExpandNodeInitially, filterValue } = props;
    const [expanded, setExpanded] = useState<boolean>(shouldExpandNodeInitially(keyPath, data, level));
    const handleClick = useCallback(() => {
        if (expandable)
            setExpanded(!expanded);
    }, [expandable, expanded]);
    const renderedChildren = expanded || (hideRoot && level === 0) ? renderChildNodes({ ...props, level: level + 1 }) : null;
    // If we have a filter and no children match, don't render this node (unless it's the root)
    if (filterValue && filterValue.trim() && !hideRoot && renderedChildren && React.Children.count(renderedChildren) === 0) {
        return null;
    }
    const renderedItemString = (<span>
            <span>{nodeTypeIndicator}</span> {createItemString(data)}
        </span>);
    return hideRoot ? (<li>
            <ul>{renderedChildren}</ul>
        </li>) : (<li className={cx(nestedNodeStyles.base, nestedNodeStyles.withKeyPath(keyPath.length))}>
            {expandable && <JSONArrow expanded={expanded} onClick={handleClick}/>}
            <label onClick={handleClick}>
                <span>{keyPath[0]}:</span>
            </label>
            <span className={cx(nestedNodeItemStringStyles.base)} onClick={handleClick}>
                {renderedItemString}
            </span>
            <ul className={nestedNodeChildrenStyles.expanded(expanded)}>{renderedChildren}</ul>
        </li>);
}
const nestedNodeStyles = {
    base: css({
        paddingTop: "0.25em",
    }),
    withKeyPath: (keyPathLength: number) => css({
        marginLeft: keyPathLength > 1 ? "0.875em" : 0,
    }),
};
const nestedNodeItemStringStyles = {
    base: css({
        cursor: "pointer",
        color: themeTokens.color.text.tertiary,
    }),
};
const nestedNodeChildrenStyles = {
    expanded: (expanded: boolean) => css({
        display: expanded ? "block" : "none",
    }),
};
